/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import {memo, useMemo} from "react";

import { useForm, FormProvider } from "react-hook-form";
import { useNavigate,useLocation  } from "react-router-dom";

import ReactLoading from 'react-loading';

// Parts
import H3 from "components/parts/title/H3";
import YubinNoAddressKj from "components/parts/YubinNoAddressKj";
import NextButton from "components/ui/button/NextButton";

// 入力フォームのstore保存
import { useStateMachine } from "little-state-machine";
import {getStore, resetStore, appointmentAction, kikiSelectAction} from "stores/actions.js";

// CSS読み込み
import { mtgGroup, mtgBody } from "assets/css/stylesCss";
import { loadingCss } from "assets/css/shchMtmr";

// GQL
import useGetQuery from "hooks/useGetQuery";
import { GET_TOKEN } from "graphql/query";

import {getDibnriVal} from "screens/KikiSelect";

/**
 * URLパラメータを取得
 */
const useQuery = () => {
  const { search } = useLocation();

  return useMemo( () => new URLSearchParams(search), [search]);
}

/**
 * ご希望日時の選択
 */
const Appointment = memo(() => {

  // 画面遷移用
  const navigate = useNavigate();

  // store取得・更新
  const { state } = useStateMachine(getStore);
  const { actions } = useStateMachine({ resetStore, appointmentAction, kikiSelectAction });

  // トークン取得
  let sessionToken = sessionStorage["token"];
  if (sessionToken === undefined || sessionToken.match(/^[0-9a-zA-Z]{32}$/) === null) sessionToken = null;
  const [getToken, error, loading] = useGetQuery(GET_TOKEN, { token: sessionToken }, "no-cache");
  if (!loading && getToken !== undefined) {
    const token = getToken.getToken.token;
    sessionStorage.setItem("token", token);
  }
  if (error) navigate("/error", { state: { errorCode: 500 }, });

  // 既に受付完了済みの場合stateを初期化
  if (state.storeConfirm.uktkNo !== "" && state.storeConfirm.uktkNo !== null) {
    actions.resetStore(state);
  }

  const methods = useForm({
    defaultValues: {
      isActive: true,
      yubinNo: state.storeAppointment.yubinNo,
      addressKj: state.storeAppointment.addressKj,
      addressKjLabel: state.storeAppointment.addressKjLabel,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    formState: { isValid },
    getValues
  } = methods;

  // URLSearchParamsから選択済み対象の機器を取得する
  const urlparam = useQuery();

  // ご希望日時の選択（カレンダー）
  const onSubmit = (data) => {
    actions.appointmentAction(data);

    // URLSearchParamsに機器選択画面で選択済みにする機器のパラメータが含まれていた場合セットする
    const selectkikis = getSelectKikiParam(urlparam);
    if (selectkikis.length !== 0){
      const selectKikiParam = [];
      selectKikiParam.selectKiki = selectkikis;
      // storeの機器選択入力値にセット
      actions.kikiSelectAction(selectKikiParam);
    };

    navigate("/appointmentCalendar", {
      state: { isSubmit: true },
    });
  };

  return (
    <>
      <h1>ご希望日時の選択</h1>
      <FormProvider {...methods}>

        {/* お申込み条件確認 */}
        <form onSubmit={methods.handleSubmit(onSubmit)}>

          <div css={mtgGroup}>
            {/** 訪問エリア */}
            <H3 title="訪問エリア" required={true}></H3>
            <p css={mtgBody}>
              訪問可能な時間帯を照会するため、ご自宅の郵便番号を入力ください。
            </p>

            {/* 郵便番号 + 都道府県・市区町村・丁目 */}
            <YubinNoAddressKj
              yubinNo="yubinNo"
              addressKj="addressKj"
              isValid={isValid}
            ></YubinNoAddressKj>
          </div>

          <NextButton
            isActive={getValues("isActive")}
            value="照会する"
          ></NextButton>
        </form>
      </FormProvider>
      {/** ローディング */}
      {loading && (
        <div css={[loadingCss]}>
          <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
        </div>
      )}
    </>
  );
});

/**
 * URLSearchParamsに含まれる選択済み機器を取得する
 * @param  urlparam URLSearchParams
 * @returns  : 選択済みにする対象機器
 */
const getSelectKikiParam = (urlparam) => {
  // 取得対象のキーを設定
  const targetKey = "selectKiki";
  //　連結文字列を設定
  const delimiter = ",";

  // 対象のパラメータを取得
  const selectKiki = urlparam.get(targetKey);
  if (selectKiki === null) return "";

  // チェック済みにする機器を分割
  const targetParam = selectKiki.split(delimiter);

  const data = [];
  targetParam.forEach(value => {
    // 大分類を取得出来ない場合不正なパラメータとして除外
    const dibnriVal = getDibnriVal(value);
    if (dibnriVal !== "" && dibnriVal !== undefined){
      // 大分類を取得出来た場合追加(重複除外)
      if (data.indexOf(value) === -1) {
        data.push(value);
      }
      if (data.indexOf(dibnriVal) === -1) {
        // 対象大分類の追加(重複除外)
        data.push(dibnriVal);
      }
    }

  });

  return data;
}
export default Appointment;
